import { EMPTY_OBJECT } from '@app/consts/common_consts';
import { AxiosError, AxiosResponse, HttpStatusCode } from 'axios';
import * as _ from 'lodash-es';
import { CommonErrorType } from '../error-handler';
import { handleError, unhandledException } from '../error-handler/AllExceptionHandler';
import { ApiRespNormalizedProps, IApiResponse, IError, IErrorDetail } from '../http';

export function httpResponseHandler<T>(error: AxiosError) {
  if (error.response) {
    return handleError<T>(error.response);
  }

  return unhandledException(error);
}

export function normalizeResponse<T>(response: AxiosResponse<IApiResponse<T>>): ApiRespNormalizedProps<T> {
  const responseData = response.data as any;

  const result: ApiRespNormalizedProps<T> = {
    httpStatus: response.status,
    success: true,
    data: responseData.data,
    // Preserve message fields from API response
    ...(responseData.field && { code: responseData.field }),
    ...(responseData.code && { code: responseData.code }),
    ...(responseData.messageJp && { messageJp: responseData.messageJp }),
    ...(responseData.messageEn && { messageEn: responseData.messageEn }),
  };

  return result;
}

export function normalizeErrorResponse<T>(error: AxiosError): ApiRespNormalizedProps<T> {
  const responseErr = (error.response?.data as any) || EMPTY_OBJECT;
  const resError = (responseErr.error as IError) || EMPTY_OBJECT;

  // Check if error is an array with validation errors
  const hasErrorArray = Array.isArray(responseErr.error) && responseErr.error.length > 0;
  const hasErrorsArray = Array.isArray(responseErr.errors) && responseErr.errors.length > 0;

  // Check if response has flat structure (code, messageJp, messageEn) or nested structure (error.code).
  // The API can return code as an empty string, so detect by key existence instead of truthiness.
  const isFlatStructure =
    !responseErr.error && _.some(['code', 'messageJp', 'messageEn'], (key) => _.has(responseErr, key));
  // error.response is undefined when browser blocks response (e.g. CORS on 401)
  // Axios 1.x exposes error.status directly as fallback
  const resolvedStatus = error.response?.status ?? (error as any).status ?? HttpStatusCode.Unauthorized;

  const result: ApiRespNormalizedProps<T> = {
    httpStatus: resolvedStatus,
    success: false,
    errorCode: isFlatStructure ? responseErr.code : resError.code,
    errorMessage: isFlatStructure ? responseErr.messageJp || responseErr.messageEn : resError.message,
    errorDetails: isFlatStructure ? undefined : resError.body,
    placeholders: isFlatStructure ? undefined : resError.details,
    // Store original messages for later use
    ...(isFlatStructure && {
      code: responseErr.code,
      messageJp: responseErr.messageJp,
      messageEn: responseErr.messageEn,
    }),
  };

  // Handle validation error array (e.g., field-level errors)
  if (hasErrorArray || hasErrorsArray) {
    const firstError = hasErrorArray ? responseErr.error[0] : responseErr.errors[0];

    Object.assign(result, {
      field: firstError.field || null,
      code: firstError.messageCode,
      messageJp: firstError.messageJp,
      messageEn: firstError.messageEn,
      errorCode: firstError.messageCode,
      errorMessage: firstError.messageJp || firstError.messageEn,
    });
  }

  // Format error if error code is INVALID_ARGUMENT_EXCEPTIONF
  if (responseErr.error?.code === CommonErrorType.INVALID_ARGUMENT_EXCEPTION) {
    const errorDetails = transformErrors(responseErr.error.body as ControlledType[]);
    result.errorDetails = errorDetails;
  }
  return result;
}

const regexArrayField = new RegExp(/\[\d+\]/g);

function getArrayIndex(keyPathStr: string) {
  const result = regexArrayField.exec(keyPathStr.trim().split('.').pop() ?? '') ?? [];
  const arrStr = result.pop();
  if (!arrStr) {
    return undefined;
  }
  return Number(arrStr.replace('[', '').replace(']', ''));
}

const transformErrors = (body: ControlledType[]): IErrorDetail[] => {
  if (!body) {
    return [];
  }

  const errorFormated: ControlledType = { base: [] };
  const errorList = body;

  // Filter for base
  const baseFieldErrors = errorList.filter((e) => !e.field?.[0].includes('.'));
  for (const error of baseFieldErrors) {
    errorFormated.base.push({
      key: error.field?.[0],
      index: undefined,
      message: error.message,
    });
  }

  // Filter for other form data
  const fieldErrors = errorList.filter((e) => e.field?.[0].includes('.'));
  for (const error of fieldErrors) {
    const fieldKeys = error.field?.[0].split('.');
    const currKeys = [];

    while (fieldKeys?.length > 0) {
      const currKey = fieldKeys.shift();
      const nextKey = fieldKeys[0];

      if (nextKey) {
        currKeys.push(currKey);
        continue;
      }

      const keyPath = currKeys.join('.');
      const index = getArrayIndex(keyPath);
      const errorObj = {
        key: currKey,
        index,
        message: error.message,
      };

      const keyPathFormated = keyPath.replace(regexArrayField, '');
      if (errorFormated[keyPathFormated]) {
        errorFormated[keyPathFormated].push(errorObj);
      } else {
        errorFormated[keyPathFormated] = [errorObj];
      }

      currKeys.push(currKey);
    }
  }

  return errorFormated;
};
